/**
 * HexUtils - Hex grid mathematics for pointy-top hexagons
 * Common functions re-exported from shared/common; client-specific functions below.
 */

// Re-export common types and functions
export type { HexCoord } from './common/HexCoord';
export { HEX_DIRECTIONS } from './common/HexCoord';
export { hexDistance, hexNeighbors, hexEquals, hexKey, parseHexKey, hexesInRange } from './common/HexUtils';
export type { FootprintSize } from './common/HexUtils';
export { STATION_FOOTPRINT, getPlanetFootprint, getFootprintHexes, distanceToFootprint, getFootprintKeySet } from './common/HexUtils';

// Import what client-specific functions need locally
import type { HexCoord } from './common/HexCoord';
import { HEX_DIRECTIONS } from './common/HexCoord';
import { hexDistance, hexEquals, hexKey, hexNeighbors } from './common/HexUtils';

// ---------- Client-only types and constants ----------

export interface PixelCoord {
  x: number;
  y: number;
}

// Hex size: width and height of a pointy-top hexagon
export const HEX_WIDTH = 100.0; // Horizontal distance between hex centers
export const HEX_HEIGHT = 86.0; // Vertical spacing
export const SQRT_3 = 1.732050808;

// Isometric Y-compression (client-only visual transform; do NOT sync to server)
export const ISO_Y_SCALE = 0.55;

// ---------- Client-only functions ----------

/**
 * Convert axial coordinates to pixel position
 */
export function axialToPixel(q: number, r: number): PixelCoord {
  const x = HEX_WIDTH * ((3.0 / 4.0) * q);
  const y = HEX_HEIGHT * (r + q / 2.0) * ISO_Y_SCALE;
  return { x, y };
}

/**
 * Convert pixel position to axial coordinates
 */
export function pixelToAxial(x: number, y: number): HexCoord {
  const yFlat = y / ISO_Y_SCALE;
  const q = ((4.0 / 3.0) * x) / HEX_WIDTH;
  const r = yFlat / HEX_HEIGHT - q / 2.0;
  return roundAxial(q, r);
}

/**
 * Round fractional axial coordinates to nearest hex
 */
export function roundAxial(q: number, r: number): HexCoord {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);

  const qDiff = Math.abs(rq - q);
  const rDiff = Math.abs(rr - r);
  const sDiff = Math.abs(rs - s);

  if (qDiff > rDiff && qDiff > sDiff) {
    rq = -rr - rs;
  } else if (rDiff > sDiff) {
    rr = -rq - rs;
  }

  return { q: rq, r: rr };
}

/**
 * A* pathfinding for hex grid (client version: no maxDistance, blocked = blocked)
 */
export function aStar(start: HexCoord, goal: HexCoord, isBlocked: (hex: HexCoord) => boolean): HexCoord[] {
  const openSet: HexCoord[] = [start];
  const cameFrom = new Map<string, HexCoord>();
  const gScore = new Map<string, number>();
  const fScore = new Map<string, number>();

  const startKey = hexKey(start);
  gScore.set(startKey, 0);
  fScore.set(startKey, hexDistance(start, goal));

  while (openSet.length > 0) {
    // Sort by f-score and get lowest
    openSet.sort((a, b) => {
      const fa = fScore.get(hexKey(a)) ?? Infinity;
      const fb = fScore.get(hexKey(b)) ?? Infinity;
      return fa - fb;
    });

    const current = openSet[0];
    const currentKey = hexKey(current);

    if (hexEquals(current, goal)) {
      // Reconstruct path
      const path: HexCoord[] = [];
      let node: HexCoord | undefined = current;
      while (node) {
        path.push(node);
        node = cameFrom.get(hexKey(node));
      }
      path.reverse();
      return path;
    }

    openSet.shift();

    for (const neighbor of hexNeighbors(current)) {
      if (isBlocked(neighbor)) {
        continue;
      }

      const neighborKey = hexKey(neighbor);
      const tentativeG = (gScore.get(currentKey) ?? Infinity) + 1;

      if (tentativeG < (gScore.get(neighborKey) ?? Infinity)) {
        cameFrom.set(neighborKey, current);
        gScore.set(neighborKey, tentativeG);
        fScore.set(neighborKey, tentativeG + hexDistance(neighbor, goal));

        if (!openSet.some((h) => hexEquals(h, neighbor))) {
          openSet.push(neighbor);
        }
      }
    }
  }

  return []; // No path found
}

/**
 * Get hexes forming a ring at a specific distance
 */
export function hexRing(center: HexCoord, radius: number): HexCoord[] {
  if (radius === 0) {
    return [center];
  }

  const results: HexCoord[] = [];
  let hex: HexCoord = {
    q: center.q + HEX_DIRECTIONS[4].q * radius,
    r: center.r + HEX_DIRECTIONS[4].r * radius,
  };

  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < radius; j++) {
      results.push(hex);
      hex = {
        q: hex.q + HEX_DIRECTIONS[i].q,
        r: hex.r + HEX_DIRECTIONS[i].r,
      };
    }
  }

  return results;
}

/**
 * Linear interpolation between two hexes
 */
export function hexLerp(a: HexCoord, b: HexCoord, t: number): { q: number; r: number } {
  return {
    q: a.q + (b.q - a.q) * t,
    r: a.r + (b.r - a.r) * t,
  };
}

/**
 * Get all hexes along a line from a to b
 */
export function hexLine(a: HexCoord, b: HexCoord): HexCoord[] {
  const distance = hexDistance(a, b);
  if (distance === 0) {
    return [a];
  }

  const results: HexCoord[] = [];
  for (let i = 0; i <= distance; i++) {
    const lerped = hexLerp(a, b, i / distance);
    results.push(roundAxial(lerped.q, lerped.r));
  }

  return results;
}
